/* eslint-disable react/prop-types */
import React, { useState } from 'react'
import FacebookLogin from 'react-facebook-login/dist/facebook-login-render-props'
import { getEnvVar } from '@mc/client-env'
import { AUTH_TYPE } from '@mc/constants'
import {
  Button,
  ButtonKinds,
  IconSocialFacebookActive,
} from '@mc/design-system'
import { isInAppBrowser } from '@mc/devices'
import {
  setCartEmailCookie,
  setFireEmailCaptureCookie,
} from '@mc/email-capture'
import { useTranslation, withI18nContext } from '@mc/i18n'
import { paths } from '@mc/paths'
import { i18nInstance } from '../../../i18n/i18nInstance'
import {
  fbAuth,
  isSuccessful,
  needsConfirmation,
  getError,
  redirect,
} from '../../../services'
import { OnClickEventProps, SocialButtonProps } from '../types'

interface FacebookRenderProps {
  onClick: () => void
  isDisabled: boolean
  isProcessing: boolean
  isSdkLoaded: boolean
}

interface FacebookButtonProps extends SocialButtonProps {
  onSuccess?: (props?: OnClickEventProps) => void
  onError?: (error: unknown) => void
}

const FacebookButtonComponent = ({
  kind = ButtonKinds.TERTIARY,
  className = '',
  text = '',
  fullWidth = false,
  onClick = () => {},
  onError = () => {},
  onSuccess = () => {},
  captureMethod,
  userAgent = '',
}: FacebookButtonProps) => {
  const [loading, setLoading] = useState(false)
  const { t } = useTranslation('@mc/authentication')
  const action = text || t('thirdParty.facebook')

  // see isInAppBrowser to understand why
  if (isInAppBrowser(userAgent)) return null

  const onFacebookClick = (
    onClickRenderProp: FacebookRenderProps['onClick'],
  ) => {
    onClickRenderProp()
    onClick({ track: { action } })
  }

  const onFacebookLogin = async () => {
    try {
      setLoading(true)

      const res = await fbAuth()
      if (isSuccessful(res)) {
        if (captureMethod) {
          setFireEmailCaptureCookie({
            authType: AUTH_TYPE.FACEBOOK,
            captureMethod,
          })
          setCartEmailCookie(res.email)
        }
        redirect(res, () => onSuccess({ track: { action } }))
      } else if (needsConfirmation(res)) {
        window.location.href = paths.authConfirmEmail
      }
    } catch (error) {
      onError(error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <FacebookLogin
      cookie
      appId={getEnvVar('FACEBOOK_KEY')}
      fields='name,email,picture'
      scope='public_profile, email'
      callback={onFacebookLogin}
      onFailure={() => onError(getError())}
      disableMobileRedirect={true}
      render={(props: FacebookRenderProps) => (
        <Button
          kind={kind}
          onClick={() => onFacebookClick(props.onClick)}
          className={className}
          loading={loading}
          fullWidth={fullWidth}
        >
          <IconSocialFacebookActive className='mc-mr-2' />
          {action}
        </Button>
      )}
    />
  )
}

export const FacebookButton = withI18nContext(i18nInstance)(
  FacebookButtonComponent,
)
